import { useEffect, useRef } from "react";

/**
 * Types a colored code snippet into a <pre> when it scrolls into view.
 * `tokens` is an array of { t: text, c: cssColor }. Reduced motion renders
 * the whole snippet at once. Returns a ref for the <pre>.
 */
const useCodeTyper = (tokens) => {
  const preRef = useRef(null);

  useEffect(() => {
    const pre = preRef.current;
    if (!pre || !Array.isArray(tokens) || tokens.length === 0) {
      return undefined;
    }

    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let started = false;
    let timer = 0;

    const run = () => {
      pre.innerHTML = "";
      const cursor = document.createElement("span");
      cursor.textContent = "▌";
      cursor.style.color = "#4d8dff";
      cursor.style.animation = "il-blink 1s step-end infinite";
      pre.appendChild(cursor);

      if (reduce) {
        tokens.forEach((tok) => {
          const s = document.createElement("span");
          s.style.color = tok.c;
          s.textContent = tok.t;
          pre.insertBefore(s, cursor);
        });
        return;
      }

      let ti = 0;
      let ci = 0;
      let span = null;
      const tick = () => {
        if (ti >= tokens.length) {
          return;
        }
        if (ci === 0) {
          span = document.createElement("span");
          span.style.color = tokens[ti].c;
          pre.insertBefore(span, cursor);
        }
        span.textContent += tokens[ti].t[ci];
        ci += 1;
        if (ci >= tokens[ti].t.length) {
          ti += 1;
          ci = 0;
        }
        timer = window.setTimeout(tick, 14);
      };
      tick();
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          started = true;
          run();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(pre);

    return () => {
      io.disconnect();
      window.clearTimeout(timer);
    };
  }, [tokens]);

  return preRef;
};

export default useCodeTyper;
